import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthField, AuthShell } from '../components/auth/AuthField';
import { NotebookCover } from '../components/auth/NotebookCover';

/** Sign in — the web twin of mobile's login screen (mobile/app/(auth)/login.tsx). */
export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(
        err?.response?.status === 401
          ? 'Incorrect email or password.'
          : (err?.response?.data?.detail as string) || 'Something went wrong. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="pb-rise flex flex-col items-center text-center">
        <NotebookCover title="Your notebook" />
        <h1 className="pb-heading-1 m-0 mt-8 text-[2.5rem] tracking-[-0.02em] md:text-[2.75rem]">Welcome back.</h1>
        <p className="m-0 mt-2 max-w-[18rem] font-body text-[0.8125rem] text-on-surface-variant">
          Everything you’ve written is right where you left it.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          required
        />
        <AuthField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          placeholder="••••••••"
          error={error}
          required
        />
        <button type="submit" className="pb-btn pb-btn--primary mt-2 w-full py-3.5" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="m-0 mt-8 text-center font-body text-[0.9375rem] text-on-surface-variant">
        New here?{' '}
        <Link to="/register" className="font-semibold text-accent no-underline hover:underline">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
};
